/**
 * WeatherAssessmentCard - Expandable card for weather assessment
 */

import React from 'react';
import { Cloud, ChevronDown, ChevronUp, CheckCircle, Wind, Eye, AlertCircle } from 'lucide-react';

const WeatherAssessmentCard = ({ 
  weather,
  setWeather,
  isExpanded,
  onToggle,
  isComplete
}) => {
  return (
    <div className={`rounded-lg border-2 transition-all ${
      isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
    }`}>
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cloud className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-gray-600'}`} />
            <div>
              <h4 className="text-sm font-medium">Weather Assessment</h4>
              <p className="text-xs text-gray-500">
                {weather.isAcceptable === true ? 'Acceptable' : 
                 weather.isAcceptable === false ? 'Not Acceptable' : 'Required'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            {/* Weather Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Weather Source
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="manual"
                    checked={weather.source === 'manual'}
                    onChange={(e) => setWeather(prev => ({
                      ...prev,
                      source: e.target.value
                    }))}
                    className="text-cyan-600"
                  />
                  <span className="text-sm">Manual Entry</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="api"
                    checked={weather.source === 'api'}
                    onChange={(e) => setWeather(prev => ({
                      ...prev,
                      source: e.target.value
                    }))}
                    className="text-cyan-600"
                  />
                  <span className="text-sm">Fetch from API</span>
                </label>
              </div>
            </div>

            {weather.source === 'api' ? (
              <div className="text-center py-4">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Fetch Weather Data
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Weather API integration coming soon
                </p>
              </div>
            ) : (
              <>
                {/* Manual Weather Entry */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <Wind className="inline h-3 w-3 mr-1" />
                      Wind Speed (knots)
                    </label>
                    <input
                      type="text"
                      value={weather.windSpeed}
                      onChange={(e) => setWeather(prev => ({
                        ...prev,
                        windSpeed: e.target.value
                      }))}
                      placeholder="e.g., 15"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Wind Direction
                    </label>
                    <input
                      type="text"
                      value={weather.windDirection}
                      onChange={(e) => setWeather(prev => ({
                        ...prev,
                        windDirection: e.target.value
                      }))}
                      placeholder="e.g., NW"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Sea State
                    </label>
                    <select
                      value={weather.seaState}
                      onChange={(e) => setWeather(prev => ({
                        ...prev,
                        seaState: e.target.value
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="0">0 - Calm</option>
                      <option value="1">1 - Calm ripples</option>
                      <option value="2">2 - Small wavelets</option>
                      <option value="3">3 - Large wavelets</option>
                      <option value="4">4 - Small waves</option>
                      <option value="5">5 - Moderate waves</option>
                      <option value="6">6 - Large waves</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      <Eye className="inline h-3 w-3 mr-1" />
                      Visibility
                    </label>
                    <select
                      value={weather.visibility}
                      onChange={(e) => setWeather(prev => ({
                        ...prev,
                        visibility: e.target.value
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="good">Good (&gt;5nm)</option>
                      <option value="moderate">Moderate (2-5nm)</option>
                      <option value="poor">Poor (&lt;2nm)</option>
                      <option value="fog">Fog</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Forecast Summary
                  </label>
                  <textarea
                    value={weather.forecast}
                    onChange={(e) => setWeather(prev => ({
                      ...prev,
                      forecast: e.target.value
                    }))}
                    placeholder="Brief weather forecast..."
                    rows={2}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </>
            )}

            {/* Weather Decision */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Weather Assessment Decision
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setWeather(prev => ({
                    ...prev,
                    isAcceptable: true
                  }))}
                  className={`px-4 py-2 rounded-md ${
                    weather.isAcceptable === true
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <CheckCircle className="inline h-4 w-4 mr-1" />
                  Acceptable
                </button>
                <button
                  onClick={() => setWeather(prev => ({
                    ...prev,
                    isAcceptable: false
                  }))}
                  className={`px-4 py-2 rounded-md ${
                    weather.isAcceptable === false
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Not Acceptable
                </button>
              </div>
              {weather.isAcceptable === false && (
                <p className="text-sm text-red-600 mt-2">
                  Voyage cannot proceed with unacceptable weather conditions
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherAssessmentCard;